import AsyncStorage from "@react-native-async-storage/async-storage";
import * as SplashScreen from "expo-splash-screen";
import React, { memo, useEffect, useState } from "react";
import { View } from "react-native";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { fetchLogout } from "../actions";
import {
	CLEAR_DROP_DOWN_ALERT_ERROR,
	CLEAR_DROP_DOWN_ALERT_INFO,
	CLEAR_DROP_DOWN_ALERT_MESSAGE,
	CLEAR_DROP_DOWN_ALERT_WARN,
	CLEAR_LOGOUT,
	LOGOUT,
	RESET_DATA
} from "../actions/types";
import { getErrorManagementSelector } from "../selectors";
import { t } from "./i18n";

const ErrorManagement = props => {
	const dispatch = useDispatch();
	const [splashScreenClosed, setSplashScreenClosed] = useState(false);

	const {
		isMaintenance,
		dropDownAlertInfo,
		dropDownUnclosedAlertInfo,
		dropDownAlertError,
		dropDownAlertMessage,
		dropDownAutoClearAlertError,
		dropDownAutoClearAlertWarn,
		dropDownAutoClearAlertInfo,
		splashScreenHide,
		logout,
		logoutResult,
		deleteUserResult,
		dropDownAlertWarn
	} = useSelector(getErrorManagementSelector, shallowEqual);

	const { navigationRef, dropDownHolder } = props;

	const navigation = navigationRef.current;

	useEffect(() => {
		if (
			(splashScreenHide || dropDownAlertError || dropDownAlertWarn) &&
			!splashScreenClosed
		) {
			(async () => {
				setSplashScreenClosed(true);
				await SplashScreen.hideAsync();
			})();
		}
	}, [
		splashScreenHide,
		splashScreenClosed,
		dropDownAlertWarn,
		dropDownAlertError
	]);

	useEffect(() => {
		if (isMaintenance) {
			navigation?.navigate("MaintenanceScreen");
		}
	}, [isMaintenance]);

	useEffect(() => {
		if (logout) {
			dispatch(fetchLogout());
		}
	}, [logout]);

	// After the account deletion (the server already closed the session): reset
	// the data to go back to the landing screen. Handled here, not in the
	// profile screen: removing the user unmounts it before its effects run.
	useEffect(() => {
		if (deleteUserResult) {
			(async () => {
				await AsyncStorage.removeItem("emailOrPseudo");
				await AsyncStorage.removeItem("hideTooltips");
				await AsyncStorage.removeItem("strategy");
				dispatch({
					type: LOGOUT
				});
				dispatch({
					type: RESET_DATA
				});
			})();
		}
	}, [deleteUserResult]);

	useEffect(() => {
		if (logoutResult) {
			dispatch({
				type: RESET_DATA
			});

			dispatch({
				type: CLEAR_LOGOUT
			});
		}
	}, [logoutResult]);

	useEffect(() => {
		// Cas des erreurs
		if (dropDownAutoClearAlertError) {
			dropDownHolder?.alertWithType(
				"error",
				t("alert:error"),
				t("login:" + dropDownAutoClearAlertError)
			);

			dispatch({
				type: CLEAR_DROP_DOWN_ALERT_ERROR
			});
		}
	}, [dropDownAutoClearAlertError]);

	useEffect(() => {
		if (dropDownAlertError) {
			dropDownHolder?.alertWithType(
				"error",
				t("alert:error"),
				t("login:" + dropDownAlertError)
			);
		}
	}, [dropDownAlertError]);

	useEffect(() => {
		if (dropDownAlertInfo) {
			dropDownHolder?.alertWithType(
				"info",
				t("alert:info"),
				t("login:" + dropDownAlertInfo)
			);
		}
	}, [dropDownAlertInfo]);

	useEffect(() => {
		if (dropDownUnclosedAlertInfo) {
			dropDownHolder?.alertWithType(
				"info",
				t("alert:info"),
				t("login:" + dropDownUnclosedAlertInfo),
				{
					dropDownUnclosedAlertInfo
				},
				30 * 1000
			);
		}
	}, [dropDownUnclosedAlertInfo]);

	useEffect(() => {
		if (dropDownAlertWarn) {
			dropDownHolder?.alertWithType(
				"warn",
				t("alert:warn"),
				t("login:" + dropDownAlertWarn),
				{
					dropDownAlertWarn
				},
				30 * 1000
			);
		}
	}, [dropDownAlertWarn]);

	useEffect(() => {
		if (dropDownAutoClearAlertWarn) {
			dropDownHolder?.alertWithType(
				"warn",
				t("alert:warn"),
				t("login:" + dropDownAutoClearAlertWarn),
				{
					dropDownAutoClearAlertWarn
				}
			);
			dispatch({
				type: CLEAR_DROP_DOWN_ALERT_WARN
			});
		}
	}, [dropDownAutoClearAlertWarn]);

	useEffect(() => {
		if (dropDownAutoClearAlertInfo) {
			// Keep the signup / password (set, reset, forgot) confirmation messages
			// on screen a bit longer (10s) than the default close interval.
			const longInfos = ["popup1", "popup2", "popup3", "resetpwdsuccess"];
			dropDownHolder?.alertWithType(
				"info",
				t("alert:info"),
				t("login:" + dropDownAutoClearAlertInfo),
				{
					dropDownAutoClearAlertInfo
				},
				longInfos.includes(dropDownAutoClearAlertInfo) ? 10000 : undefined
			);
			dispatch({
				type: CLEAR_DROP_DOWN_ALERT_INFO
			});
		}
	}, [dropDownAutoClearAlertInfo]);

	useEffect(() => {
		if (dropDownAlertMessage) {
			dropDownHolder?.alertWithType(
				"custom",
				dropDownAlertMessage.title,
				dropDownAlertMessage.message,
				dropDownAlertMessage
			);

			dispatch({
				type: CLEAR_DROP_DOWN_ALERT_MESSAGE
			});
		}
	}, [dropDownAlertMessage]);

	// Empty view
	return <View />;
};

const MemoizedErrorManagement = memo(ErrorManagement);

export default MemoizedErrorManagement;
